import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import {
  HiOutlineKey,
  HiOutlineCheck,
  HiOutlineMail,
  HiOutlineServer,
  HiOutlineLink,
  HiOutlineTag,
  HiOutlineChartBar,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

function AdminSettings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('tmdb');

  // TMDB State
  const [tmdbKey, setTmdbKey] = useState('');
  const [keyStatus, setKeyStatus] = useState({ configured: false, masked_key: null });

  // SMTP State
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_server: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'My Series',
    smtp_use_tls: true,
  });
  const [smtpStatus, setSmtpStatus] = useState({ configured: false });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  // System Version State
  const [systemVersion, setSystemVersion] = useState('1.4.1');

  // Registration State
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  // Dashboard Stats State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showTop10Mode, setShowTop10Mode] = useState(false);
  const [top10Stats, setTop10Stats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpLoaded, setSmtpLoaded] = useState(false);

  useEffect(() => {
    loadTMDBData();
    loadSMTPData();
    loadSystemVersion();
    loadRegistrationStatus();
  }, []);

  const loadSystemVersion = async () => {
    try {
      const res = await settingsAPI.getSystemVersion();
      setSystemVersion(res.data.version);
    } catch (error) {
      console.error('Error loading system version:', error);
    }
  };

  const loadRegistrationStatus = async () => {
    try {
      const res = await settingsAPI.getRegistrationStatus();
      setRegistrationEnabled(res.data.enabled);
    } catch (error) {
      console.error('Error loading registration status:', error);
    }
  };

  const loadDashboardStats = async (period, dateFrom, dateTo) => {
    const p = period || statsPeriod;
    const from = dateFrom !== undefined ? dateFrom : customDateFrom;
    const to = dateTo !== undefined ? dateTo : customDateTo;
    setStatsLoading(true);
    try {
      const params = { period: p };
      if (p === 'custom' && from) params.date_from = from;
      if (p === 'custom' && to) params.date_to = to;
      const res = await settingsAPI.getDashboardStats(params);
      setDashboardStats(res.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      toast.error(t('settings.errorLoadingDashboard'));
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTop10Stats = async (period, dateFrom, dateTo) => {
    const p = period || statsPeriod;
    const from = dateFrom !== undefined ? dateFrom : customDateFrom;
    const to = dateTo !== undefined ? dateTo : customDateTo;
    setStatsLoading(true);
    try {
      const params = { period: p };
      if (p === 'custom' && from) params.date_from = from;
      if (p === 'custom' && to) params.date_to = to;
      const res = await settingsAPI.getTop10Stats(params);
      setTop10Stats(res.data);
    } catch (error) {
      console.error('Error loading top10 stats:', error);
      toast.error(t('settings.errorLoadingDashboard'));
    } finally {
      setStatsLoading(false);
    }
  };

  const formatStatDate = (dateStr) => {
    if (!dateStr) return t('settings.noData');
    try {
      const date = new Date(dateStr);
      const brazilTime = new Date(date.getTime() - (3 * 60 * 60 * 1000));
      const day = String(brazilTime.getDate()).padStart(2, '0');
      const month = String(brazilTime.getMonth() + 1).padStart(2, '0');
      const year = brazilTime.getFullYear();
      const hours = String(brazilTime.getHours()).padStart(2, '0');
      const minutes = String(brazilTime.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateStr;
    }
  };

  const loadTMDBData = async () => {
    try {
      const keyRes = await settingsAPI.getTMDBKeyStatus();
      setKeyStatus(keyRes.data);
    } catch (error) {
      console.error('Error loading TMDB settings:', error);
    }
  };

  const loadSMTPData = async () => {
    if (smtpLoaded) return; // Only load once

    try {
      const smtpRes = await settingsAPI.getSMTPStatus();
      setSmtpStatus(smtpRes.data);
      setIsConfigured(smtpRes.data.configured);
      setSmtpLoaded(true);

      // Load saved config from API and localStorage for password
      const savedPassword = localStorage.getItem('smtp_password') || '';

      if (smtpRes.data.configured) {
        setSmtpConfig({
          smtp_server: smtpRes.data.smtp_server || '',
          smtp_port: smtpRes.data.smtp_port || '587',
          smtp_username: smtpRes.data.smtp_username || '',
          smtp_password: savedPassword,  // Load from localStorage
          smtp_from_email: smtpRes.data.smtp_from_email || '',
          smtp_from_name: smtpRes.data.smtp_from_name || 'My Series',
          smtp_use_tls: smtpRes.data.smtp_use_tls !== undefined ? smtpRes.data.smtp_use_tls : true,
        });
      } else {
        // Not configured - set defaults
        setSmtpConfig({
          smtp_server: '',
          smtp_port: '587',
          smtp_username: '',
          smtp_password: savedPassword,  // Load from localStorage
          smtp_from_email: '',
          smtp_from_name: 'My Series',
          smtp_use_tls: true,
        });
      }
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (e) => {
    e.preventDefault();
    if (!tmdbKey.trim()) {
      toast.error(t('settings.apiKey'));
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateTMDBKey(tmdbKey);
      toast.success(t('settings.updateKey'));
      setTmdbKey('');
      loadTMDBData();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSMTP = async (e) => {
    e.preventDefault();
    if (!smtpConfig.smtp_server || !smtpConfig.smtp_username ||
        !smtpConfig.smtp_password || !smtpConfig.smtp_from_email) {
      toast.error(t('users.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateSMTP(smtpConfig);
      // Save password to localStorage
      localStorage.setItem('smtp_password', smtpConfig.smtp_password);
      toast.success(t('common.success'));
      setIsConfigured(true);
      // Don't reload SMTP data - keep password in field
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error(t('settings.testEmailAddress'));
      return;
    }

    setSendingTest(true);
    try {
      await settingsAPI.testSMTPEmail(testEmail);
      toast.success(t('settings.emailSent'));
      setTestEmail('');
    } catch (error) {
      toast.error(getErrorMessage(error) || t('settings.emailError'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await settingsAPI.testSMTPConnection();
      toast.success(response.data.message || t('settings.connectionSuccess'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('settings.connectionError'));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveVersion = async (e) => {
    e.preventDefault();
    if (!systemVersion.trim()) {
      toast.error(t('settings.systemVersionEmpty'));
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateSystemVersion(systemVersion);
      toast.success(t('settings.systemVersionUpdated'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRegistration = async () => {
    setSaving(true);
    try {
      const newStatus = !registrationEnabled;
      await settingsAPI.updateRegistrationStatus(newStatus);
      setRegistrationEnabled(newStatus);
      toast.success(t('settings.registrationStatusUpdated'));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
        <p>{t('settings.title')}</p>
      </div>

      {/* Tabs */}
      <div className="tabs tabs--no-arrow">
        <button
          className={`tab ${activeTab === 'tmdb' ? 'active' : ''}`}
          onClick={() => setActiveTab('tmdb')}
        >
          TMDB API
        </button>
        <button
          className={`tab ${activeTab === 'smtp' ? 'active' : ''}`}
          onClick={() => setActiveTab('smtp')}
        >
          {t('settings.smtp')}
        </button>
        <button
          className={`tab ${activeTab === 'registration' ? 'active' : ''}`}
          onClick={() => setActiveTab('registration')}
        >
          {t('settings.registrationTab')}
        </button>
        <button
          className={`tab ${activeTab === 'version' ? 'active' : ''}`}
          onClick={() => setActiveTab('version')}
        >
          {t('settings.systemVersionTab')}
        </button>
        <button
          className={`tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => { setActiveTab('statistics'); loadDashboardStats(); }}
        >
          <HiOutlineChartBar style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          {t('settings.statisticsTab')}
        </button>
      </div>

      {/* TMDB API Tab */}
      {activeTab === 'tmdb' && (
        <div className="card">
          <div className="card-header">
            <h2>{t('settings.tmdbApiKey')}</h2>
          </div>

          <div style={{ padding: '0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                background: keyStatus.configured
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(245, 158, 11, 0.1)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: keyStatus.configured ? 'var(--success)' : 'var(--warning)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                {keyStatus.configured ? <HiOutlineCheck size={20} /> : <HiOutlineKey size={20} />}
              </div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '2px' }}>
                  {keyStatus.configured ? t('settings.configured') : t('settings.notConfigured')}
                </h4>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {keyStatus.configured
                    ? `${t('settings.apiKey')}: ${keyStatus.masked_key.substring(0, 4)}*****${keyStatus.masked_key.substring(keyStatus.masked_key.length - 4)}`
                    : t('dashboard.configureApiKey')}
                  </p>
              </div>
            </div>

            <form onSubmit={handleSaveKey}>
              <div className="form-group">
                <label className="form-label">{t('settings.tmdbApiKey')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={tmdbKey}
                  onChange={(e) => setTmdbKey(e.target.value)}
                  placeholder={
                    keyStatus.configured
                      ? t('settings.updateKey')
                      : t('settings.apiKey')
                  }
                />
                <p
                  style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    marginTop: '8px',
                  }}
                >
                  <a
                    href="https://www.themoviedb.org/settings/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary)' }}
                  >
                    themoviedb.org
                  </a>
                </p>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !tmdbKey.trim()}
              >
                {saving ? t('profile.saving') : t('settings.updateKey')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* SMTP Tab */}
      {activeTab === 'smtp' && (
        <div className="card">
          <div className="card-header">
            <h2>{t('settings.smtp')}</h2>
          </div>

          <div style={{ padding: '0' }}>
            {/* SMTP Status */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                background: smtpStatus.configured
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(245, 158, 11, 0.1)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: smtpStatus.configured ? 'var(--success)' : 'var(--warning)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <HiOutlineMail size={20} />
              </div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '2px' }}>
                  {smtpStatus.configured ? t('settings.configured') : t('settings.notConfigured')}
                </h4>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {smtpStatus.configured
                    ? `${t('settings.smtpHost')}: ${smtpStatus.smtp_server}:${smtpStatus.smtp_port}`
                    : t('settings.smtp')}
                  </p>
              </div>
            </div>

            <form onSubmit={handleSaveSMTP}>
              <div className="form-group">
                <label className="form-label">{t('settings.smtpHost')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={smtpConfig.smtp_server}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_server: e.target.value })}
                  placeholder="smtp.gmail.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('settings.smtpPort')} *</label>
                <input
                  type="number"
                  className="form-input"
                  value={smtpConfig.smtp_port}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: e.target.value })}
                  placeholder="587"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('settings.smtpUser')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={smtpConfig.smtp_username}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_username: e.target.value })}
                  placeholder="email@gmail.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('settings.smtpPassword')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={smtpConfig.smtp_password}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })}
                  placeholder={t('settings.smtpPassword')}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('settings.smtpFrom')} *</label>
                <input
                  type="email"
                  className="form-input"
                  value={smtpConfig.smtp_from_email}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_email: e.target.value })}
                  placeholder="noreply@domain.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.name')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={smtpConfig.smtp_from_name}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_name: e.target.value })}
                  placeholder="My Series"
                />
              </div>

              <div
                className="checkbox-container"
                onClick={() => setSmtpConfig({ ...smtpConfig, smtp_use_tls: !smtpConfig.smtp_use_tls })}
                style={{ marginBottom: '20px' }}
              >
                <div className={`checkbox ${smtpConfig.smtp_use_tls ? 'checked' : ''}`}>
                  {smtpConfig.smtp_use_tls && <HiOutlineCheck size={14} />}
                </div>
                <span>TLS/SSL</span>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ marginRight: '12px' }}
              >
                {saving ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </form>

            {/* Test SMTP Section */}
            <div
              style={{
                marginTop: '24px',
                padding: '16px',
                background: 'var(--background)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
              }}
            >
              <h4 style={{ marginBottom: '12px', fontWeight: '600' }}>
                {t('settings.testConnection')}
              </h4>

              {/* Connection Test */}
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {t('settings.testConnection')}
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                  style={{ width: '100%' }}
                >
                  <HiOutlineLink style={{ marginRight: '6px' }} />
                  {testingConnection ? t('common.loading') : t('settings.testConnection')}
                </button>
              </div>

              {/* Email Test */}
              <div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {t('settings.testEmail')}
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="email"
                    className="form-input"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder={t('settings.testEmailAddress')}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !testEmail}
                  >
                    {sendingTest ? t('auth.sending') : t('settings.testEmail')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Tab */}
      {activeTab === 'registration' && (
        <div className="card">
          <div className="card-header">
            <h2>{t('settings.registrationTab')}</h2>
          </div>

          <div style={{ padding: '0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                background: registrationEnabled
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: registrationEnabled ? 'var(--success)' : 'var(--danger)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <HiOutlineCheck size={20} />
              </div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '2px' }}>
                  {registrationEnabled ? t('common.enabled') : t('common.disabled')}
                </h4>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {t('settings.registrationDescription')}
                </p>
              </div>
            </div>

            <div style={{ padding: '0 16px 16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: 'var(--radius)',
                  background: 'var(--card-bg)',
                  border: '2px solid var(--border-color)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <input
                  type="checkbox"
                  checked={registrationEnabled}
                  onChange={handleToggleRegistration}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontWeight: '500' }}>
                  {t('settings.registrationEnabled')}
                </span>
              </label>
            </div>

            <div
              style={{
                padding: '12px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: 'var(--radius)',
                fontSize: '14px',
                color: 'var(--text-secondary)',
              }}
            >
              <strong style={{ color: 'var(--text-color)' }}>Info:</strong>{' '}
              {registrationEnabled
                ? t('settings.registrationEnabledInfo')
                : t('settings.registrationDisabledInfo')}
            </div>
          </div>
        </div>
      )}

      {/* System Version Tab */}
      {activeTab === 'version' && (
        <div className="card">
          <div className="card-header">
            <h2>{t('settings.systemVersionTab')}</h2>
          </div>

          <div style={{ padding: '0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                background: 'rgba(124, 58, 237, 0.1)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <HiOutlineTag size={20} />
              </div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '2px' }}>
                  {t('settings.title')}
                </h4>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {t('settings.systemVersionDescription')}
                </p>
              </div>
            </div>

            <form onSubmit={handleSaveVersion} style={{ padding: '0 16px 16px' }}>
              <div className="form-group">
                <label className="form-label">{t('common.version')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={systemVersion}
                  onChange={(e) => setSystemVersion(e.target.value)}
                  placeholder="Ex: 1.4.1"
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !systemVersion.trim()}
              >
                {saving ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h2>{t('settings.statisticsTab')}</h2>
              {/* Toggle Top 10 Mode - Below Period Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: showTop10Mode ? 'var(--text-muted)' : 'var(--text-color)' }}>
                  {t('settings.cardsMode')}
                </span>
                <label className="toggle-switch toggle-switch--small">
                  <input
                    type="checkbox"
                    checked={showTop10Mode}
                    onChange={(e) => {
                      setShowTop10Mode(e.target.checked);
                      if (e.target.checked) {
                        loadTop10Stats();
                      }
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span style={{ fontSize: '14px', fontWeight: '500', color: showTop10Mode ? 'var(--text-color)' : 'var(--text-muted)' }}>
                  {t('settings.top10Mode')}
                </span>
              </div>
            </div>
            {/* Period Filter in Header */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {['all', 'today', 'week', 'month', 'custom'].map((p) => (
                <button
                  key={p}
                  className={`btn ${statsPeriod === p ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                  onClick={() => {
                    setStatsPeriod(p);
                    if (p !== 'custom') {
                      if (showTop10Mode) {
                        loadTop10Stats(p);
                      } else {
                        loadDashboardStats(p);
                      }
                    }
                  }}
                >
                  {t(`settings.period_${p}`)}
                </button>
              ))}
              {statsPeriod === 'custom' && (
                <>
                  <input
                    type="date"
                    className="form-input"
                    style={{ padding: '6px 10px', fontSize: '13px', width: 'auto' }}
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                  />
                  <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>—</span>
                  <input
                    type="date"
                    className="form-input"
                    style={{ padding: '6px 10px', fontSize: '13px', width: 'auto' }}
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    style={{ padding: '6px 14px', fontSize: '13px' }}
                    onClick={() => {
                      if (showTop10Mode) {
                        loadTop10Stats('custom', customDateFrom, customDateTo);
                      } else {
                        loadDashboardStats('custom', customDateFrom, customDateTo);
                      }
                    }}
                    disabled={!customDateFrom || !customDateTo}
                  >
                    {t('settings.filterApply')}
                  </button>
                </>
              )}
            </div>
          </div>

          {statsLoading ? (
            <div className="loading-container" style={{ padding: '40px' }}>
              <div className="spinner"></div>
            </div>
          ) : showTop10Mode && top10Stats ? (
            /* Top 10 Mode - 4 Columns */
            <div style={{ padding: '16px' }}>
              <div className="top10-grid">
                {/* Top 10 Movies */}
                <div className="top10-column top10-movies">
                  <div className="top10-header">
                    <div className="top10-icon">🎬</div>
                    <h3 className="top10-title">{t('settings.top10Movies')}</h3>
                  </div>
                  <div className="top10-list">
                    {top10Stats.top10_movies.length > 0 ? (
                      top10Stats.top10_movies.map((item, index) => (
                        <div key={index} className={`top10-item ${index < 3 ? 'top10-podium' : ''}`}>
                          <span className="top10-rank">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : <span className="top10-rank-number">#{index + 1}</span>}
                          </span>
                          <span className="top10-name">{item.title}</span>
                          <span className="top10-badge">{item.count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="top10-empty">{t('settings.noData')}</p>
                    )}
                  </div>
                </div>

                {/* Top 10 TV Shows */}
                <div className="top10-column top10-series">
                  <div className="top10-header">
                    <div className="top10-icon">📺</div>
                    <h3 className="top10-title">{t('settings.top10Series')}</h3>
                  </div>
                  <div className="top10-list">
                    {top10Stats.top10_tv.length > 0 ? (
                      top10Stats.top10_tv.map((item, index) => (
                        <div key={index} className={`top10-item ${index < 3 ? 'top10-podium' : ''}`}>
                          <span className="top10-rank">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : <span className="top10-rank-number">#{index + 1}</span>}
                          </span>
                          <span className="top10-name">{item.title}</span>
                          <span className="top10-badge">{item.count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="top10-empty">{t('settings.noData')}</p>
                    )}
                  </div>
                </div>

                {/* Top 10 Users by Logins */}
                <div className="top10-column top10-logins">
                  <div className="top10-header">
                    <div className="top10-icon">👥</div>
                    <h3 className="top10-title">{t('settings.top10UsersLogins')}</h3>
                  </div>
                  <div className="top10-list">
                    {top10Stats.top10_users_logins.length > 0 ? (
                      top10Stats.top10_users_logins.map((item, index) => (
                        <div key={index} className={`top10-item ${index < 3 ? 'top10-podium' : ''}`}>
                          <span className="top10-rank">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : <span className="top10-rank-number">#{index + 1}</span>}
                          </span>
                          <span className="top10-name">{item.username}</span>
                          <span className="top10-badge">{item.count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="top10-empty">{t('settings.noData')}</p>
                    )}
                  </div>
                </div>

                {/* Top 10 Users by Items Added */}
                <div className="top10-column top10-items">
                  <div className="top10-header">
                    <div className="top10-icon">📊</div>
                    <h3 className="top10-title">{t('settings.top10UsersItems')}</h3>
                  </div>
                  <div className="top10-list">
                    {top10Stats.top10_users_items.length > 0 ? (
                      top10Stats.top10_users_items.map((item, index) => (
                        <div key={index} className={`top10-item ${index < 3 ? 'top10-podium' : ''}`}>
                          <span className="top10-rank">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : <span className="top10-rank-number">#{index + 1}</span>}
                          </span>
                          <span className="top10-name">{item.username}</span>
                          <span className="top10-badge">{item.count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="top10-empty">{t('settings.noData')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : dashboardStats ? (
            /* Cards Mode */
            <div style={{ padding: '16px' }}>
              <div className="stats-dashboard">
                {/* 1. Total Users / Active Users */}
                <div className="stat-card">
                  <div className="stat-card-icon purple">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.total_users}</h3>
                    <p>{t('settings.totalUsersCard')}</p>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {dashboardStats.active_users} {t('settings.activeLabel')}
                    </span>
                  </div>
                </div>

                {/* 2. Total Movies */}
                <div className="stat-card">
                  <div className="stat-card-icon teal">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.total_movies}</h3>
                    <p>{t('settings.totalMoviesCard')}</p>
                  </div>
                </div>

                {/* 3. Total TV Shows */}
                <div className="stat-card">
                  <div className="stat-card-icon pink">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.total_tv_shows}</h3>
                    <p>{t('settings.totalTvShowsCard')}</p>
                  </div>
                </div>

                {/* 4. Total Watched Episodes */}
                <div className="stat-card">
                  <div className="stat-card-icon blue">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.total_watched_episodes}</h3>
                    <p>{t('settings.totalWatchedEpisodes')}</p>
                  </div>
                </div>

                {/* 5. TMDB API Calls */}
                <div className="stat-card">
                  <div className="stat-card-icon orange">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.tmdb_calls_month}</h3>
                    <p>{t('settings.tmdbCalls')}</p>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {t(`settings.period_${statsPeriod}`)}
                      {statsPeriod === 'custom' && customDateFrom && customDateTo &&
                        ` (${customDateFrom.split('-').reverse().join('/')} - ${customDateTo.split('-').reverse().join('/')})`
                      }
                    </span>
                  </div>
                </div>

                {/* 6. Most Active User (month) */}
                <div className="stat-card">
                  <div className="stat-card-icon purple">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <p>{t('settings.mostActiveUser')}</p>
                    <h3>{dashboardStats.most_active_user?.username || t('settings.noData')}</h3>
                    {dashboardStats.most_active_user && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {dashboardStats.most_active_user.login_count} logins
                      </span>
                    )}
                  </div>
                </div>

                {/* 7. User with Most Movies */}
                <div className="stat-card">
                  <div className="stat-card-icon teal">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <p>{t('settings.topMoviesUser')}</p>
                    <h3>{dashboardStats.top_movies_user?.username || t('settings.noData')}</h3>
                    {dashboardStats.top_movies_user && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {dashboardStats.top_movies_user.count} {t('settings.movies')}
                      </span>
                    )}
                  </div>
                </div>

                {/* 8. User with Most TV Shows */}
                <div className="stat-card">
                  <div className="stat-card-icon pink">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <p>{t('settings.topTvUser')}</p>
                    <h3>{dashboardStats.top_tv_user?.username || t('settings.noData')}</h3>
                    {dashboardStats.top_tv_user && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {dashboardStats.top_tv_user.count} {t('settings.series')}
                      </span>
                    )}
                  </div>
                </div>

                {/* 9. Last Registered User */}
                <div className="stat-card">
                  <div className="stat-card-icon blue">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <p>{t('settings.lastRegisteredUser')}</p>
                    <h3>{dashboardStats.last_registered_user?.username || t('settings.noData')}</h3>
                    {dashboardStats.last_registered_user?.created_at && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {formatStatDate(dashboardStats.last_registered_user.created_at)}
                      </span>
                    )}
                  </div>
                </div>

                {/* 10. Most Registered Title */}
                <div className="stat-card stat-card--title">
                  <div className="stat-card-icon orange">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <p>{t('settings.mostRegisteredTitle')}</p>
                    <h3 title={dashboardStats.most_registered_title?.title || ''}>{dashboardStats.most_registered_title?.title || t('settings.noData')}</h3>
                    {dashboardStats.most_registered_title && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {dashboardStats.most_registered_title.count} {t('settings.usersLabel')} · {dashboardStats.most_registered_title.media_type === 'movie' ? t('settings.movieLabel') : t('settings.tvLabel')}
                      </span>
                    )}
                  </div>
                </div>

                {/* 11. Completion Rate */}
                <div className="stat-card">
                  <div className="stat-card-icon teal">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div>
                    <h3>{dashboardStats.completion_rate}%</h3>
                    <p>{t('settings.completionRate')}</p>
                  </div>
                </div>

                {/* 12. TV Show with Most Watched Episodes */}
                <div className="stat-card stat-card--title">
                  <div className="stat-card-icon pink">
                    <HiOutlineChartBar size={20} />
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <p>{t('settings.topTvEpisodes')}</p>
                    <h3 title={dashboardStats.top_tv_episodes?.title || ''}>{dashboardStats.top_tv_episodes?.title || t('settings.noData')}</h3>
                    {dashboardStats.top_tv_episodes && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {dashboardStats.top_tv_episodes.episodes_watched} {t('settings.episodesWatched')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Instructions */}
      {activeTab === 'tmdb' && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <h2>{t('settings.tmdb')}</h2>
          </div>

          <div style={{ lineHeight: '1.8' }}>
            <h4 style={{ marginBottom: '12px', fontWeight: '600' }}>
              {t('settings.tmdbApiKey')}
            </h4>
            <ol
              style={{
                paddingLeft: '20px',
                color: 'var(--text-secondary)',
                fontSize: '14px',
              }}
            >
              <li>
                <a
                  href="https://www.themoviedb.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--primary)' }}
                >
                  themoviedb.org
                </a>
              </li>
              <li>{t('settings.apiKey')}</li>
            </ol>
          </div>
        </div>
      )}

      {/* Bottom spacer for mobile */}
      <div className="admin-settings__bottom-spacer" />
    </div>
  );
}

export default AdminSettings;
